import { PlusOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Checkbox, DatePicker, Form, Input, InputNumber, Select, TreeSelect } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import adGroupApi from 'src/component/app/adGroup/AdGroupApi'
import * as routes from 'src/constants/routes'
import { useUser } from 'src/context/UserContext'
import { AdGroupBean, CampaignBean, FilterListBean, LanguageBean } from '../../../api/api'
import { formDataLoader, FormDataType } from '../../../util/FormDataLoader'
import { capitalizedView, getIabCategoriesAsTree, isAdmin } from '../../../util/Util'
import GeoTargeting, { GeoItem, toBean } from '../geo/GeoTargeting'
import { MapPoint, MapSelector } from '../map/MapSelector'
import { WithLoadedMap } from '../map/WithLoadedMap'
import { ALWAYS_WORK, clear, renderDay, Scheduler } from '../schedule/Scheduler'
import './style.css'

const FormItem = Form.Item
const Option = Select.Option

const precisionEnum = AdGroupBean.GeoPrecisionEnum

export const getGeoPrecision = (geoPrecision: AdGroupBean.GeoPrecisionEnum) => {
    switch (geoPrecision) {
        case precisionEnum.GPSLocation: return 'GPS/Location'
        case precisionEnum.IPAddress: return 'IP address'
        case precisionEnum.GPSLocationAndIP: return 'GPS/Location and IP'
    }
}

const AdGroupCreate = () => {

    const formInfoItems: FormDataType[] = [
        FormDataType.Languages,
        FormDataType.Campaigns,
        FormDataType.IabCategories
    ]

    const { user } = useUser()

    const [info, setInfo] = useState({
        languages: [],
        campaigns: [],
        iabCategories: [],
        filterLists: []
    })

    const navigateTo = useNavigate()
    const [error, setError] = useState(undefined)
    const [form] = Form.useForm()
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')

    const [geoItems, setGeoItems] = useState<GeoItem[]>([])

    const [schedule, setSchedule] = useState<number[][]>(ALWAYS_WORK)
    const [scheduleVisible, setScheduleVisible] = useState(false)

    const [mapPoint, setMapPoint] = useState<MapPoint>({})
    const [mapVisible, setMapVisible] = useState(false)

    const admin = isAdmin(user)

    const loadData = async () => {
        try {
            if (admin) formInfoItems.push(FormDataType.FilterLists)

            const resp = await formDataLoader(...formInfoItems)
            setInfo(resp as any)
        } catch (e) {
            setError(e.error)
        }
    }

    useEffect(() => {
        loadData()
    }, [])

    const handleSubmit = async (path: string) => {
        const values = await form.validateFields()
        try {
            const response = await adGroupApi.create({ ...values, startDate, endDate, geoItems: geoItems.map(toBean), ...mapPoint, schedule: clear(schedule) })
            const id = response.data?.id

            navigateTo(path ? `${path}/${id}` : '/ad-group')
        } catch (e) {
            setError(e.error)
        }
    }

    return <Form
        style={{
            maxWidth: '1000px', margin: '0 auto'
        }}
        onFinish={handleSubmit}
        form={form}
        labelCol={{ span: 5 }}
        wrapperCol={{ span: 19 }}
    >
        {error ? <Alert message={error} type="error" /> : null}
        <Card title="General" size="small" className="form-card">
            <FormItem
                required
                name="name"
                label="Name"
            >
                <Input />
            </FormItem>
            <FormItem
                name="status"
                label="Status"
                required
            >
                <Select>
                    {Object.values(AdGroupBean.StatusEnum).map(type => <Option key={type} value={type}>{capitalizedView(type)}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                name="campaignId"
                required
                label="Campaign"
            >
                <Select
                    showSearch
                    optionFilterProp="title"
                    placeholder="Select Campaign"
                >
                    {info.campaigns.map((item: CampaignBean) => <Option title={item.name} key={item.id} value={item.id}>{item.name}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                required
                name="domain"
                label="Domain"
            >
                <Input />
            </FormItem>
            <FormItem
                name="iabCategoryIds"
                label="IAB Categories"
            >
                <TreeSelect
                    allowClear
                    treeCheckable
                    showCheckedStrategy="SHOW_ALL"
                    placeholder="Select IAB categories"
                    treeData={getIabCategoriesAsTree(info.iabCategories)}
                />
            </FormItem>
            <FormItem
                required
                label="Dates"
            >
                <FormItem
                    name="startDate"
                    className="ant-item ant-item-first"
                >
                    <DatePicker showTime={{ defaultValue: dayjs('00:00', 'HH:mm') }} format="YYYY-MM-DD HH:mm" onChange={(moment, value) => setStartDate(value)} placeholder="Start date" />
                </FormItem>
                <FormItem
                    name="endDate"
                    className="ant-item ant-item-second"
                >
                    <DatePicker showTime={{ defaultValue: dayjs('00:00', 'HH:mm') }} format="YYYY-MM-DD HH:mm" onChange={(moment, value) => setEndDate(value)} placeholder="End date" />
                </FormItem>
            </FormItem>
            <FormItem
                label="Frequency Capping"
            >
                <FormItem
                    name="fcPeriod"
                    className="fc ant-item ant-item-first"
                >
                    <Select allowClear placeholder="Select FC period">
                        {Object.keys(AdGroupBean.FcPeriodEnum).map(type => <Option key={type} value={type}>{type}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="fcLimit"
                    className="ant-item ant-item-second"
                >
                    <InputNumber placeholder="FC limit" />
                </FormItem>
            </FormItem>
        </Card>
        <Card title="Budget" size="small" className="form-card">
            <FormItem
                required
                label="Total Budget"
            >
                <FormItem
                    name="totalBudget"
                    className="ant-item ant-item-first"
                >
                    <InputNumber />
                </FormItem>
                <FormItem className="ant-item ant-item-second">$</FormItem>
            </FormItem>
            <FormItem
                required
                name="distribution"
                label="Distribution"
            >
                <Select placeholder="Select Distribution Type">
                    {Object.values(AdGroupBean.DistributionEnum).map(type => <Option key={type} value={type}>{type}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                required
                name="biddingStrategy"
                label="Bidding Strategy"
            >
                <Select
                    allowClear
                    placeholder="Select Bidding Strategy">
                    {Object.values([AdGroupBean.BiddingStrategyEnum.CPM]).map(type => (
                        <Option key={type} value={type}>{type}</Option>))}
                </Select>
            </FormItem>
            <FormItem
                required
                label="Price"
            >
                <FormItem
                    name="price"
                    className="ant-item ant-item-first"
                >
                    <InputNumber />
                </FormItem>
                <FormItem className="ant-item ant-item-second">$</FormItem>
            </FormItem>
        </Card>
        <Card title="Creatives" size="small" className="form-card">
            <FormItem label="Linked Creatives">
                <Select mode="multiple" />
            </FormItem>
            <FormItem wrapperCol={{ offset: 5, span: 19 }}>
                <Button htmlType="button" type="primary" ghost style={{ width: '23.8%', margin: '0 1% 0 0', borderRadius: '16px' }} icon={<PlusOutlined />} onClick={e => handleSubmit('/creative/create')}>
                    New Creative
                </Button>
                <Button htmlType="button" type="primary" ghost style={{ width: '23.8%', borderRadius: '16px' }} icon={<PlusOutlined />} onClick={e => handleSubmit('/creative/bulk')}>
                    Create bulk
                </Button>
            </FormItem>
        </Card>
        <Card title="Targeting" size="small" className="form-card">
            <FormItem
                name="languages"
                label="Languages"
            >
                <Select
                    showSearch
                    allowClear
                    mode="multiple"
                    optionFilterProp="title"
                    placeholder="Select language"
                >
                    {info.languages.map((item: LanguageBean) => <Option title={item.name} key={item.id} value={item.id}>{item.name}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                name="devices"
                label="Devices"
            >
                <Select mode="multiple" placeholder="Select device">
                    {Object.keys(AdGroupBean.DevicesEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                name="brands"
                label="Brands"
            >
                <Select mode="multiple" placeholder="Select brand">
                    {Object.keys(AdGroupBean.BrandsEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                name="platforms"
                label="Platforms"
            >
                <Select mode="multiple" placeholder="Select platform">
                    {Object.keys(AdGroupBean.PlatformsEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                name="browsers"
                label="Browsers"
            >
                <Select mode="multiple" placeholder="Select browser">
                    {Object.keys(AdGroupBean.BrowsersEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                name="contentIabCategoryIds"
                label="Content IAB Categories"
            >
                <TreeSelect
                    allowClear
                    treeCheckable
                    showCheckedStrategy="SHOW_ALL"
                    placeholder="Select content IAB categories"
                    treeData={getIabCategoriesAsTree(info.iabCategories)}
                />
            </FormItem>
            <FormItem
                name="dealOnly"
                label="PMP only"
                valuePropName="checked"
            >
                <Checkbox />
            </FormItem>
            <FormItem
                name="dealIdList"
                label="Deal ID list"
            >
                <Input />
            </FormItem>
            {admin && <FormItem
                name="filterListIds"
                label="Filter Lists"
            >
                <Select
                    showSearch
                    allowClear
                    mode="multiple"
                    optionFilterProp="title"
                    placeholder="Select Filter List"
                >
                    {info.filterLists.map((item: FilterListBean) => <Option title={item.name} key={item.id} value={item.id as number}>{item.name}</Option>)}
                </Select>
            </FormItem>}
        </Card>
        <Card title="Day Parting" size="small" className="form-card">
            <FormItem label="Monday">{renderDay(schedule[0])}</FormItem>
            <FormItem label="Tuesday">{renderDay(schedule[1])}</FormItem>
            <FormItem label="Wednesday">{renderDay(schedule[2])}</FormItem>
            <FormItem label="Thursday">{renderDay(schedule[3])}</FormItem>
            <FormItem label="Friday">{renderDay(schedule[4])}</FormItem>
            <FormItem label="Saturday">{renderDay(schedule[5])}</FormItem>
            <FormItem label="Sunday">{renderDay(schedule[6])}</FormItem>
            <FormItem wrapperCol={{ offset: 5, span: 19 }}>
                <Button htmlType="button" type="primary" ghost style={{ width: '23.8%', borderRadius: '16px' }} onClick={() => setScheduleVisible(true)}>
                    Set day parting
                </Button>
            </FormItem>
            <Scheduler
                schedule={schedule}
                visible={scheduleVisible}
                setSchedule={setSchedule}
                setVisible={setScheduleVisible}
            />
        </Card>
        <Card title="Geo" size="small" className="form-card">
            <FormItem label="Coordinates" >
                <FormItem
                    className="ant-item ant-item-first"
                >
                    <InputNumber value={mapPoint.lat} placeholder="Latitude" />
                </FormItem>
                <FormItem
                    className="ant-item ant-item-second"
                >
                    <InputNumber value={mapPoint.lon} placeholder="Longitude" />
                </FormItem>
                <FormItem
                    className="ant-item ant-item-second"
                >
                    <InputNumber value={mapPoint.radius} placeholder="Radius" />
                </FormItem>
                <FormItem
                    className="ant-item ant-item-second"
                >
                    <Button htmlType="button" type="primary" ghost style={{ width: '14.5rem', borderRadius: '16px' }} onClick={() => setMapVisible(true)} >
                        Select on map
                    </Button>
                </FormItem>
            </FormItem>
            <WithLoadedMap>
                <MapSelector
                    point={mapPoint}
                    setPoint={setMapPoint}
                    mapVisible={mapVisible}
                    setMapVisible={setMapVisible}
                />
            </WithLoadedMap>
            <FormItem
                name="geoPrecision"
                label="Geo Precision"
            >
                <Select>
                    {Object.values(precisionEnum).map((type: CampaignBean.GeoPrecisionEnum) => <Option key={type} value={type}>{getGeoPrecision(type)}</Option>)}
                </Select>
            </FormItem>
            <GeoTargeting
                id={undefined}
                loadGeo={undefined}
                geoItems={geoItems}
                setGeoItems={setGeoItems}
            />
        </Card>
        <div className="create-cancel-group">
            <Button type="primary" htmlType="submit">Create</Button>
            <Button htmlType="button" danger onClick={() => navigateTo(routes.AD_GROUP)}>Cancel</Button>
        </div>
    </Form>
}

export default AdGroupCreate


// WEBPACK FOOTER //
// ./src/component/app/adGroup/AdGroupCreate.tsx